/** Campo de senha com medidor de força e lista do que ainda falta.
 *
 *  Usado onde uma senha NOVA é criada: primeiro acesso e troca de senha. O campo
 *  de senha atual continua sendo um TextField comum, porque medir a força de uma
 *  senha que já existe não ajuda ninguém.
 *
 *  A avaliação é a mesma do backend, em `passwordPolicy.ts`. Aqui só existe a
 *  apresentação: dizer o que falta enquanto a pessoa digita, em vez de recusar
 *  no envio com um toast, que era o comportamento antigo.
 */
import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import { Check, Eye, EyeOff } from 'lucide-react'
import { useI18n } from '@/core/i18n/useI18n'
import {
  CLASSES_EXIGIDAS,
  COMPRIMENTO_MINIMO,
  avaliar,
  type ChaveDeClasse,
  type DadosDoDono,
  type NivelDeForca,
} from './passwordPolicy'

const COR_DO_NIVEL: Record<NivelDeForca, string> = {
  fraca: 'var(--color-danger)',
  media: 'var(--color-warning)',
  forte: 'var(--color-success)',
}

const SEGMENTOS_DO_NIVEL: Record<NivelDeForca, number> = { fraca: 1, media: 2, forte: 3 }

const CLASSES_EM_ORDEM: ChaveDeClasse[] = ['maiuscula', 'minuscula', 'numero', 'simbolo']

interface Props {
  value: string
  onChange: (valor: string) => void
  label: string
  dono?: DadosDoDono
  autoFocus?: boolean
  disabled?: boolean
  /** Some com o medidor enquanto o campo está vazio e nunca foi tocado. */
  id?: string
}

export function CampoSenhaForte({ value, onChange, label, dono, autoFocus, disabled, id }: Props) {
  const { t } = useI18n()
  const [visivel, setVisivel] = useState(false)
  const avaliacao = avaliar(value, dono)
  const mostrarMedidor = value.length > 0

  const rotuloDaClasse: Record<ChaveDeClasse, string> = {
    maiuscula: t('form.campoSenhaForte.classUppercase'),
    minuscula: t('form.campoSenhaForte.classLowercase'),
    numero: t('form.campoSenhaForte.classNumber'),
    simbolo: t('form.campoSenhaForte.classSymbol'),
  }

  const motivo = avaliacao.motivo
  const aviso =
    motivo === 'previsivel'
      ? t('form.campoSenhaForte.tooPredictable')
      : motivo === 'dados-pessoais'
        ? t('form.campoSenhaForte.personalData')
        : null

  return (
    <Stack spacing={1}>
      <TextField
        id={id}
        label={label}
        type={visivel ? 'text' : 'password'}
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        autoFocus={autoFocus}
        disabled={disabled}
        fullWidth
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => setVisivel((v) => !v)}
                  aria-label={visivel ? t('form.campoSenhaForte.hide') : t('form.campoSenhaForte.show')}
                >
                  {visivel ? <EyeOff size={17} /> : <Eye size={17} />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      {mostrarMedidor && (
        <Stack spacing={0.75}>
          {/* Medidor: três segmentos, preenchidos conforme o nível. */}
          <Stack direction="row" spacing={0.5} aria-hidden>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  height: 4,
                  flex: 1,
                  borderRadius: 'var(--radius-full)',
                  transition: 'background-color var(--transition-quick)',
                  backgroundColor:
                    i < SEGMENTOS_DO_NIVEL[avaliacao.nivel] && (avaliacao.valida || i === 0)
                      ? COR_DO_NIVEL[avaliacao.nivel]
                      : 'var(--color-surface-strong)',
                }}
              />
            ))}
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: COR_DO_NIVEL[avaliacao.nivel], fontWeight: 600 }}
            role="status"
          >
            {t(`form.campoSenhaForte.level.${avaliacao.nivel}`)}
          </Typography>

          {aviso ? (
            <Typography variant="caption" sx={{ color: 'var(--color-danger-fg)' }}>
              {aviso}
            </Typography>
          ) : (
            <Stack spacing={0.25}>
              <Requisito
                atendido={avaliacao.comprimentoOk}
                texto={t('form.campoSenhaForte.minLength', { minimo: COMPRIMENTO_MINIMO })}
              />
              <Requisito
                atendido={avaliacao.classes.length >= CLASSES_EXIGIDAS}
                texto={t('form.campoSenhaForte.mixTypes', {
                  exigidas: CLASSES_EXIGIDAS,
                  tipos: CLASSES_EM_ORDEM.map((c) => rotuloDaClasse[c]).join(', '),
                })}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  )
}

function Requisito({ atendido, texto }: { atendido: boolean; texto: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      <Box
        sx={{
          width: 14,
          height: 14,
          mt: '2px',
          flexShrink: 0,
          borderRadius: 'var(--radius-full)',
          display: 'grid',
          placeItems: 'center',
          color: atendido ? 'var(--color-primary-fg)' : 'var(--color-text-subtle)',
          backgroundColor: atendido ? 'var(--color-success)' : 'var(--color-surface-strong)',
        }}
        aria-hidden
      >
        {atendido && <Check size={10} strokeWidth={3} />}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: atendido ? 'var(--color-text-muted)' : 'var(--color-text-subtle)' }}
      >
        {texto}
      </Typography>
    </Stack>
  )
}
